import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom, Observable } from 'rxjs';
import { PaginatedResponse } from '../../../core/models/paginated-response.model';
import { BulkUploadResult, Client, ClientEnrollment, FamilyMember } from '../models/clients.model';
import { Task } from '../../tasks/models/tasks.model';
import { Opportunity } from '../../opportunities/models/opportunity.model';
import { Lead } from '../../leads/models/lead.model';
import { API_BASE_URL } from '../../../core/constants';
import { BranchContextService } from '../../../core/services/branch-context.service';

@Injectable({
  providedIn: 'root'
})
export class ClientService {
  private http = inject(HttpClient);
  private branchContext = inject(BranchContextService);
  private apiUrl = `${API_BASE_URL}/clients`;
  private leadApiUrl = `${API_BASE_URL}/leads`;
  private familyMemberApiUrl = `${API_BASE_URL}/family-members`;

  getClients(
    filter?: { status?: string; search?: string, page: number, limit: number, sortBy?: string, sortOrder?: string }
  ): Observable<PaginatedResponse<Client>> {
    let params = new HttpParams()

    // Add filters if they exist
    if (filter) {
      Object.entries(filter).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          params = params.set(key, value.toString());
        }
      });
    }

    if (this.branchContext.selectedBranchId()) {
      params = params.set("branchId", this.branchContext.selectedBranchId()!);
    }

    return this.http.get<PaginatedResponse<Client>>(this.apiUrl, { params });
  }

  exportClients(
    filter?: { status?: string; search?: string, page: number, limit: number, sortBy?: string, sortOrder?: string }
  ): Observable<any> {
    let params = new HttpParams()

    // Add filters if they exist
    if (filter) {
      Object.entries(filter).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          params = params.set(key, value.toString());
        }
      });
    }

    if (this.branchContext.selectedBranchId()) {
      params = params.set("branchId", this.branchContext.selectedBranchId()!);
    }

    params = params.set('format', 'csv')

    return this.http.get(this.apiUrl, { params, responseType: 'blob' });
  }

  createClient(clientData: Omit<any, 'id'>): Observable<any>;
  createClient(clientData: FormData): Observable<any>;
  createClient(clientData: any): Observable<any> {
    return this.http.post(this.apiUrl, clientData);
  }

  updateClient(id: string, updates: Partial<any>): Observable<any>;
  updateClient(id: string, updates: FormData): Observable<any>;
  updateClient(id: string, updates: any): Observable<any> {
    return this.http.patch(`${this.apiUrl}/${id}`, updates);
  }

  getClientById(id: string): Observable<Client> {
    return this.http.get<Client>(`${this.apiUrl}/${id}`);
  }

  getLeadForConversion(id: String): Observable<Lead> {
    return this.http.get<Lead>(`${this.leadApiUrl}/${id}`);
  }

  getTasksByClient(id: string): Observable<Task[]> {
    return this.http.get<Task[]>(`${this.apiUrl}/${id}/tasks`);
  }

  getClientTimeline(id: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/${id}/timeline`);
  }

  getOpportunitiesByClient(id: string): Observable<Opportunity[]> {
    return this.http.get<Opportunity[]>(`${this.apiUrl}/${id}/opportunities`);
  }

  /**
   * Searches every client the user can access (by name, email, phone, PAN or UCC),
   * ignoring the selected branch — family members can sit in other branches.
   */
  searchClients(search: string, limit = 10): Observable<PaginatedResponse<Client>> {
    const params = new HttpParams()
      .set('search', search)
      .set('scope', 'ALL_CLIENTS')
      .set('page', '1')
      .set('limit', limit.toString());
    return this.http.get<PaginatedResponse<Client>>(this.apiUrl, { params });
  }

  linkFamilyMember(memberId: string, clientId: string): Observable<FamilyMember> {
    return this.http.post<FamilyMember>(`${this.familyMemberApiUrl}/${memberId}/link`, { clientId });
  }

  unlinkFamilyMember(memberId: string): Observable<FamilyMember> {
    return this.http.delete<FamilyMember>(`${this.familyMemberApiUrl}/${memberId}/link`);
  }

  addFamilyMember(clientId: string, familyMemberData: Omit<FamilyMember, 'id' | 'createdAt' | 'updatedAt' | 'primaryClientId' | 'linkedClientId' | 'deletedAt'>): Observable<FamilyMember> {
    return this.http.post<FamilyMember>(`${this.apiUrl}/${clientId}/family-members`, familyMemberData);
  }

  getFamilyMembers(clientId: string): Observable<FamilyMember[]> {
    return this.http.get<FamilyMember[]>(`${this.apiUrl}/${clientId}/family-members`);
  }

  updateFamilyMember(memberId: string, updates: Partial<FamilyMember>): Observable<FamilyMember> {
    return this.http.patch<FamilyMember>(`${this.familyMemberApiUrl}/${memberId}`, updates);
  }

  deleteFamilyMember(memberId: string): Observable<void> {
    return this.http.delete<void>(`${this.familyMemberApiUrl}/${memberId}`);
  }

  getEnrollments(clientId: string): Observable<ClientEnrollment[]> {
    return this.http.get<ClientEnrollment[]>(`${this.apiUrl}/${clientId}/enrollments`);
  }

  updateEnrollments(clientId: string, enrollments: { opportunityTypeId: string; enrolled: boolean }[]): Observable<ClientEnrollment[]> {
    return this.http.put<ClientEnrollment[]>(`${this.apiUrl}/${clientId}/enrollments`, { enrollments });
  }

  bulkUploadClients(file: File): Promise<BulkUploadResult> {
    const formData = new FormData();
    formData.append('clientfile', file);

    return firstValueFrom(
      this.http.post<BulkUploadResult>(`${this.apiUrl}/bulk-upload`, formData)
    );
  }
}